import SmartLeadImage from "./SmartLeadImage";
import AdSlot from "./AdSlot";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, ArrowRight, TrendingUp } from "lucide-react";
import { NEWSROOM_MORE, SECONDARY, MOST_READ, getHeroLead, getPrevLead } from "../data/stories";
import { fetchTrending } from "../api";
import { timeAgo } from "../utils/time";
import Reveal from "./Reveal";
import { titleCaseHeadline } from "../utils/titleCase";

const word = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.05, ease: [0.19, 1, 0.22, 1] },
  }),
};

/* Word-level masked reveal: wraps naturally at any width, never forces breaks */
const MaskedWords = ({ text, testid, className }) => {
  const words = text.split(" ");
  return (
    <h1 data-testid={testid} className={className}>
      {words.map((w, i) => (
        <span key={i}>
          <span
            aria-hidden="true"
            className="inline-block overflow-hidden pb-[0.14em] -mb-[0.14em] align-bottom"
          >
            <motion.span
              className="inline-block"
              variants={word}
              initial="hidden"
              animate="show"
              custom={i}
            >
              {w}
            </motion.span>
          </span>
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </h1>
  );
};

const LiveDot = () => (
  <span className="relative flex h-2 w-2" data-testid="most-read-live-indicator">
    <span className="animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full bg-[#cf0a0a] opacity-60" />
    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#cf0a0a]" />
  </span>
);

const ArticleCard = ({ article, testid }) => (
  <article data-testid={testid} className="group h-full">
    <Link to={`/${article.id}`} className="block">
      <div className="img-frame overflow-hidden aspect-[16/10] bg-[var(--card-muted)]">
        <img
          src={article.img}
          alt={article.imgAlt}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
      <p className="mt-4 flex items-center justify-between gap-3">
        <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[var(--accent)]">
          {article.kicker}
        </span>
        <span className="text-[11px] text-[var(--faint)] whitespace-nowrap">
          {article.date}
        </span>
      </p>
      <h3 className="mt-1.5 font-display text-xl sm:text-[1.35rem] leading-snug line-clamp-2 font-semibold group-hover:text-[var(--accent)] transition-colors duration-300">
        {titleCaseHeadline(article.title)}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-[var(--muted)] line-clamp-2">{article.excerpt}</p>
    </Link>
  </article>
);

export const HeroSection = ({ categoryStories = null, adPanel = false, showSecondary = true }) => {
  const imgWrap = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgWrap,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  /* Lead slots come from the CMS front page (curated edition as fallback) */
  const hero = categoryStories ? categoryStories[0] : getHeroLead();
  const prevLead = categoryStories ? categoryStories[1] : getPrevLead();
  const newsroomLinks = categoryStories ? categoryStories.slice(2, 5) : NEWSROOM_MORE;

  const [trending, setTrending] = useState(null);
  useEffect(() => {
    let on = true;
    const load = () =>
      fetchTrending(MOST_READ.map((m) => m.id)).then((d) => on && d && setTrending(d));
    load();
    const t = setInterval(load, 20000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  const lastHourViews = (id) =>
    trending && trending.series[id] ? trending.series[id][11] : 0;

  const rankedMostRead = MOST_READ.map((item, i) => ({ ...item, editorial: i }))
    .sort((a, b) => lastHourViews(b.id) - lastHourViews(a.id) || a.editorial - b.editorial)
    .map((item, i) => ({ ...item, rank: String(i + 1).padStart(2, "0") }));

  /* Fastest climber: biggest hour-over-hour view jump */
  let risingId = null;
  if (trending) {
    let best = 0;
    for (const item of MOST_READ) {
      const s = trending.series[item.id] || [];
      const delta = (s[11] || 0) - (s[10] || 0);
      if (delta > best) {
        best = delta;
        risingId = item.id;
      }
    }
  }

  return (
    <section id={categoryStories ? undefined : "news"} data-testid={categoryStories ? "category-hero-section" : "hero-section"} className="bg-[var(--bg)]">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-10 sm:pt-14 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Column 1 — large lead story */}
          <div className="md:col-span-2 lg:col-span-6">
            <article>
              <Link to={`/${hero.id}`} className="block" data-testid="hero-lead-link">
                <div
                  ref={imgWrap}
                  className="img-frame relative overflow-hidden aspect-[16/10] bg-[var(--card-muted)]"
                >
                  <SmartLeadImage story={hero} loading="eager" ratio={16 / 10} />
                  {!categoryStories && <span className="absolute top-0 left-0 bg-[#cf0a0a] text-white text-[10px] font-extrabold uppercase tracking-[0.22em] px-3 py-2">
                    LEADING
                  </span>}
                </div>

                <p className="mt-5 flex items-center justify-between gap-3 text-[11px] font-extrabold uppercase tracking-[0.22em] text-[var(--accent)]">
                  {!categoryStories && <span className="flex items-center gap-3">
                    {hero.kicker}
                    <span className="h-px w-10 bg-[#cf0a0a]/40" />
                  </span>}
                  <span
                    className="flex items-center gap-1.5 text-[var(--faint)] normal-case tracking-normal text-[11px] font-medium"
                    data-testid="hero-lead-timeago"
                  >
                    <Clock size={12} /> {timeAgo(hero.date)}
                  </span>
                </p>

                <MaskedWords
                  text={titleCaseHeadline(hero.title)}
                  testid="hero-lead-title"
                  className="mt-3 font-display font-semibold text-[2.1rem] leading-[1.05] sm:text-4xl lg:text-[2.75rem] tracking-[-0.01em]"
                />

                <p className="mt-4 text-[15px] sm:text-base leading-relaxed text-[var(--muted)] line-clamp-3">
                  {hero.excerpt}
                </p>

              </Link>
            </article>
          </div>

          {/* Column 2 — previously leading story + newsroom links */}
          {prevLead && <div className="md:col-span-1 lg:col-span-3 lg:border-l lg:border-[var(--line)] lg:pl-8">
            <Reveal>
              <Link to={`/${prevLead.id}`} className="block group" data-testid="hero-prev-lead">
                <div className="img-frame overflow-hidden aspect-[16/10] bg-[var(--card-muted)]">
                  <img
                    src={prevLead.img}
                    alt={prevLead.imgAlt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="mt-4 flex items-center justify-between gap-3">
                  {!categoryStories && <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[var(--accent)]">
                    {prevLead.kicker}
                  </span>}
                  <span className="text-[11px] text-[var(--faint)] whitespace-nowrap">
                    {prevLead.date}
                  </span>
                </p>
                <h2 className="mt-1.5 font-display text-2xl leading-snug font-semibold group-hover:text-[var(--accent)] transition-colors duration-300">
                  {titleCaseHeadline(prevLead.title)}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-[var(--muted)] line-clamp-3">
                  {prevLead.excerpt.split(/\s+/).slice(0, 28).join(" ")}{prevLead.excerpt.split(/\s+/).length > 28 ? "…" : ""}
                </p>
              </Link>
            </Reveal>

            <ul className="mt-6 border-t border-[var(--line)] divide-y divide-[var(--line)]">
              {newsroomLinks.map((item) => (
                <li key={item.id}>
                  <Reveal>
                    <Link
                      to={`/${item.id}`}
                      data-testid={`hero-more-${item.id}`}
                      className="block py-3 group/link"
                    >
                      <span className="block text-[14px] leading-snug font-medium transition-colors duration-300 group-hover/link:text-[var(--accent)]">
                        {titleCaseHeadline(item.title)}
                      </span>
                      <span className="mt-1 block text-[10px] text-[var(--faint)]">{item.date}</span>
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>}

          {/* Column 3 — stories on home, reserved ad space on categories */}
          {adPanel ? <aside aria-label="Category advertisements" data-testid="category-hero-ad" className="md:col-span-1 lg:col-span-3 self-start"><AdSlot placement="article-sidebar-top" boxed /><AdSlot placement="article-sidebar-bottom" boxed /></aside> : <aside
            data-testid="most-read-panel"
            className="md:col-span-1 lg:col-span-3"
          >
            <div className="border border-[var(--line)] bg-[var(--bg-soft)] p-5 h-full flex flex-col">
              <div className="flex items-center gap-2.5">
                <h2 className="font-display text-xl font-semibold whitespace-nowrap">
                  Latest Stories
                </h2>

                <LiveDot />
                <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[var(--faint)]">Live</span>
              </div>

              <ol className="mt-3 divide-y divide-[var(--line)] flex-1">
                {rankedMostRead.map((item, i) => (
                  <li key={item.id}>
                    <Link
                      to={`/${item.id}`}
                      data-testid={`most-read-item-${item.rank}`}
                      className="flex items-center gap-3 py-3 group"
                    >
                      <div className="img-frame w-[68px] h-[52px] shrink-0 overflow-hidden bg-[var(--card-muted)]"><img src={item.img || '/story-placeholder.svg'} alt="" loading="lazy" className="h-full w-full object-cover" /></div>
                      <span className="min-w-0">
                        <span className="block text-[14px] leading-snug font-medium transition-colors duration-300 group-hover:text-[var(--accent)]">
                          {titleCaseHeadline(item.title)}
                        </span>
                        <span className="mt-1 flex items-center gap-2">
                          <span className="text-[10px] text-[var(--faint)]">{item.date}</span>
                          {item.id === risingId && (
                            <span
                              data-testid="rising-fast-badge"
                              className="inline-flex items-center gap-1 bg-[#cf0a0a] text-white text-[8px] font-extrabold uppercase tracking-[0.14em] px-1.5 py-0.5"
                            >
                              <TrendingUp size={9} /> Rising fast
                            </span>
                          )}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>}
        </div>

        {/* Six secondary top stories */}
        {showSecondary && <div className="mt-12 pt-10 border-t-2 border-[var(--divider)] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {SECONDARY.map((article, i) => (
            <Reveal key={article.id} delay={(i % 3) * 0.08} className="h-full">
              <ArticleCard article={article} testid={`secondary-story-${article.id}`} />
            </Reveal>
          ))}
        </div>}
      </div>
    </section>
  );
};

export default HeroSection;
